<template>
  <ModalModule
    v-model="isOpen"
    :title="title"
    :click-to-close="false"
    :esc-to-close="false"
    :show-close-button="false"
  >
    <div class="d-flex flex-column align-items-center text-center py-4">
      <ProgressBar
        mode="indeterminate"
        :class="$style.progressBar"
        class="mb-4 w-100"
      />
      <h3 class="mb-4">{{ title }}</h3>
      <p class="text-muted">{{ message }}</p>
    </div>
  </ModalModule>
</template>

<script setup lang="ts">
import { ModalModule } from '@hypercodestudio/basler-components';
import { ProgressBar } from 'primevue';

interface Props {
  modelValue: boolean;
  locale: string;
  store: string;
  maxAttempts?: number;
  pollInterval?: number;
  minDisplayTime?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxAttempts: 12,
  pollInterval: 5000,
  minDisplayTime: 10000
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'account-created': [];
  timeout: [];
  error: [statusCode: number];
}>();

const { $textDictionary } = useNuxtApp();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
});

const title = computed(
  () =>
    $textDictionary.value['auth.accountCreation.title'] ??
    'Your account is being created'
);

const message = computed(
  () =>
    $textDictionary.value['auth.accountCreation.message'] ??
    'Please wait a moment while we set up your account. This may take up to 60 seconds...'
);

// Polling logic
const pollTimer = ref<ReturnType<typeof setInterval> | null>(null);
const modalOpenedAt = ref<number>(0);
let attempts = 0;

const checkMagentoAccount = async () => {
  try {
    // Refresh the session to update magentoToken
    const { refresh } = useOidcAuth();
    await refresh();

    // Now try to fetch customer data - if successful, account exists
    await $fetch('/api/magento/customer', {
      method: 'GET',
      query: {
        locale: props.locale,
        store: props.store
      }
    });

    // Account successfully created!
    stopPolling();

    // Check if minimum display time has elapsed
    const elapsedTime = Date.now() - modalOpenedAt.value;
    const remainingTime = props.minDisplayTime - elapsedTime;

    if (remainingTime > 0) {
      // Wait for the minimum display time before closing
      setTimeout(() => {
        isOpen.value = false;
        emit('account-created');
      }, remainingTime);
    } else {
      // Minimum time already elapsed, close immediately
      isOpen.value = false;
      emit('account-created');
    }
  } catch (error: any) {
    // Analyze error type
    const statusCode = error?.response?.status || error?.statusCode || 0;

    // 401 Unauthorized or 404 Not Found: Account not yet created, continue polling
    if (statusCode === 401 || statusCode === 404) {
      attempts++;

      if (attempts >= props.maxAttempts) {
        // Timeout reached after all attempts
        stopPolling();

        // Check if minimum display time has elapsed
        const elapsedTime = Date.now() - modalOpenedAt.value;
        const remainingTime = props.minDisplayTime - elapsedTime;

        if (remainingTime > 0) {
          // Wait for the minimum display time before closing
          setTimeout(() => {
            isOpen.value = false;
            emit('timeout');
          }, remainingTime);
        } else {
          // Minimum time already elapsed, close immediately
          isOpen.value = false;
          emit('timeout');
        }
      }
      return; // Continue polling
    }

    // Server errors (500, 503) or other critical errors: Stop immediately
    if (statusCode >= 500 || statusCode === 0) {
      stopPolling();
      isOpen.value = false;
      emit('error', statusCode);
      return;
    }

    // Other errors (403, 400, etc.): Also stop immediately
    stopPolling();
    isOpen.value = false;
    emit('error', statusCode);
  }
};

const startPolling = () => {
  attempts = 0;
  modalOpenedAt.value = Date.now();
  pollTimer.value = setInterval(checkMagentoAccount, props.pollInterval);
  // Also check immediately
  checkMagentoAccount();
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

// Watch for modal opening to start polling
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      startPolling();
    } else {
      stopPolling();
    }
  },
  { immediate: true }
);

// Cleanup on unmount
onBeforeUnmount(() => {
  stopPolling();
});
</script>

<style module>
.progressBar {
  height: 6px !important;
}

.progressBar :global(.p-progressbar-value) {
  background-color: #4ec624 !important;
}
</style>
